// Trésorerie (Pilotage) — grille prévisionnelle par poste/mois et ventes par mois.
// Tables réservées aux rôles admin/gerant (RLS app.is_admin()) : les salaires
// nominatifs y figurent. Import Excel/CSV = chargement initial uniquement,
// la grille se maintient ensuite directement dans le CRM.

import {
  estMotifParDefaut,
  MOTIF_PAR_DEFAUT,
  type RegleClassement,
  type SensRegle,
} from '@imp/shared/classement-mouvements';
import { BOM_UTF8, csvDepuisLignes } from '@imp/shared/csv';
import { colonneAbsente, suiteApresRefus, tableAbsente } from '@imp/shared/erreurs-supabase';
import {
  analyserListingVentes,
  cellTexte,
  dateExiste,
  type LigneVenteImport,
  parseMontantCellule,
  versDateIso,
} from '@imp/shared/extrabat';
import { rapprocherMouvements } from '@imp/shared/rapprochement';
import {
  analyserClasseurBancaire,
  analyserReleveBancaire,
  decoderTexteBancaire,
  type AnalyseReleve,
} from '@imp/shared/releve-bancaire';

import { lireTout } from '@/lib/pagination';
import { supabase } from '@/lib/supabase';
import { ORG_ID } from '@/modules/clients/api';

// Ré-export : la page consomme ces analyseurs via ce module.
export { analyserListingVentes, parseMontantCellule, type LigneVenteImport };

export const MOIS_LABELS = [
  'Janv',
  'Févr',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juil',
  'Août',
  'Sept',
  'Oct',
  'Nov',
  'Déc',
] as const;

export interface CategorieMeta {
  id: string;
  label: string;
  // 'charge' entre dans le total des frais ; 'info' est affiché à part.
  type: 'charge' | 'info';
  /** Fond du bandeau de la famille. */
  bandeau: string;
  /** Filet vertical rappelant la famille sur chacune de ses lignes. */
  filet: string;
}

/**
 * Code couleur des familles de postes.
 *
 * Règle tenue partout dans le module : LA TEINTE SERT À SE REPÉRER, le rouge
 * et le vert sont réservés au SENS de l'argent (perte / gain, solde négatif).
 * Colorer une famille en rouge ferait perdre au rouge sa valeur d'alerte.
 *
 * Les classes sont écrites en toutes lettres : Tailwind ne conserve que les
 * noms qu'il voit dans le code, une classe assemblée à l'exécution
 * disparaîtrait à la construction.
 */
export const CATEGORIES: readonly CategorieMeta[] = [
  {
    id: 'personnel',
    label: 'Personnel',
    type: 'charge',
    bandeau: 'bg-indigo-50 text-indigo-900',
    filet: 'border-l-2 border-l-indigo-300',
  },
  {
    id: 'pub',
    label: 'Publicité & communication',
    type: 'charge',
    bandeau: 'bg-fuchsia-50 text-fuchsia-900',
    filet: 'border-l-2 border-l-fuchsia-300',
  },
  {
    id: 'vehicules',
    label: 'Véhicules',
    type: 'charge',
    bandeau: 'bg-amber-50 text-amber-900',
    filet: 'border-l-2 border-l-amber-300',
  },
  {
    id: 'autres',
    label: 'Autres frais fixes',
    type: 'charge',
    bandeau: 'bg-slate-100 text-slate-900',
    filet: 'border-l-2 border-l-slate-300',
  },
  {
    id: 'exceptionnel',
    label: 'Frais exceptionnels',
    type: 'charge',
    bandeau: 'bg-orange-50 text-orange-900',
    filet: 'border-l-2 border-l-orange-300',
  },
  {
    id: 'fournisseurs',
    label: 'Fournisseurs & sous-traitance',
    type: 'charge',
    bandeau: 'bg-teal-50 text-teal-900',
    filet: 'border-l-2 border-l-teal-300',
  },
  {
    // Seule famille en vert, et c'est cohérent : ce sont des entrées d'argent.
    id: 'encaissements',
    label: 'Encaissements (acomptes, clients)',
    type: 'info',
    bandeau: 'bg-emerald-50 text-emerald-900',
    filet: 'border-l-2 border-l-emerald-400',
  },
  {
    // Ni une charge ni une recette : de l'argent qui change de compte, ou un
    // chèque qui revient impayé. Le compter en charge ferait mentir le total
    // des frais ; le laisser sans poste laissait le rapprochement incomplet.
    id: 'internes',
    label: 'Mouvements internes',
    type: 'info',
    bandeau: 'bg-stone-50 text-stone-900',
    filet: 'border-l-2 border-l-stone-300',
  },
  {
    id: 'solde',
    label: 'Solde bancaire fin de mois',
    type: 'info',
    bandeau: 'bg-sky-50 text-sky-900',
    filet: 'border-l-2 border-l-sky-300',
  },
];

// ----- Les deux lignes que le relevé réclame ----------------------------------
//
// La grille ne contenait que des charges, plus « ACOMPTES TTC » : les
// virements des clients et les remises de chèques n'avaient aucune ligne où
// aller, et restaient « sans poste » — 859 175,99 € d'entrées au 06/10/2026.

export const POSTE_ENCAISSEMENTS_CLIENTS = 'ENCAISSEMENTS CLIENTS';
export const POSTE_MOUVEMENTS_INTERNES = 'MOUVEMENTS INTERNES';

/**
 * Le sens d'un poste : ce qui l'augmente. Un poste d'encaissement monte avec
 * les entrées ; tout autre poste — charges, mouvements internes — avec les
 * sorties. Sert aux mouvements rangés à la main : un chèque impayé rangé
 * dans les encaissements doit les diminuer.
 */
export function sensDuPoste(categorie: string): SensRegle {
  return categorie === 'encaissements' ? 'credit' : 'debit';
}

/** La règle « toute entrée non reconnue », si elle existe. */
export function regleEntreesParDefaut(regles: readonly RegleStockee[]): RegleStockee | null {
  return (
    regles.find((r) => {
      const motifs = motifsDe(r.motif);
      return r.sens === 'credit' && motifs.length > 0 && motifs.every(estMotifParDefaut);
    }) ?? null
  );
}

/**
 * Ajoute ce qui manque, et rien d'autre : les deux lignes dans la grille de
 * l'année, et la règle par défaut. Ce qui existe déjà n'est pas touché.
 *
 * Les postes sont par année (ils se recréent chaque janvier) ; la règle, elle,
 * vise le poste par son nom et vaut pour toutes les années.
 */
export async function installerLignesReleve(
  annee: number,
  manque: { encaissements: boolean; internes: boolean; regle: boolean },
  ordreRegle: number,
): Promise<void> {
  if (manque.encaissements)
    await createTresoPoste(annee, 'encaissements', POSTE_ENCAISSEMENTS_CLIENTS, 900);
  if (manque.internes) await createTresoPoste(annee, 'internes', POSTE_MOUVEMENTS_INTERNES, 950);
  if (manque.regle)
    await createRegleClassement({
      poste: POSTE_ENCAISSEMENTS_CLIENTS,
      motif: MOTIF_PAR_DEFAUT,
      sens: 'credit',
      ordre: ordreRegle,
      montant_min: null,
      montant_max: null,
    });
}

export interface TresoPoste {
  id: string;
  annee: number;
  categorie: string;
  poste: string;
  ordre: number;
  treso_montants: { id: string; mois: number; montant: string }[];
}

export async function listTresoPostes(annee: number): Promise<TresoPoste[]> {
  const { data, error } = await supabase
    .from('treso_postes')
    .select('id,annee,categorie,poste,ordre,treso_montants(id,mois,montant)')
    .eq('annee', annee)
    .is('deleted_at', null)
    .order('categorie')
    .order('ordre')
    .order('poste')
    .overrideTypes<TresoPoste[], { merge: false }>();
  if (error) throw new Error(error.message);
  return data;
}

export async function createTresoPoste(
  annee: number,
  categorie: string,
  poste: string,
  ordre: number,
): Promise<void> {
  const { error } = await supabase
    .from('treso_postes')
    .insert({ organization_id: ORG_ID, annee, categorie, poste, ordre });
  if (error) throw new Error(error.message);
}

export async function deleteTresoPoste(id: string): Promise<void> {
  // Suppression réelle : les montants suivent (FK on delete cascade).
  const { error } = await supabase.from('treso_postes').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export async function upsertTresoMontant(
  posteId: string,
  mois: number,
  montant: number,
): Promise<void> {
  const { error } = await supabase
    .from('treso_montants')
    .upsert(
      { organization_id: ORG_ID, poste_id: posteId, mois, montant },
      { onConflict: 'poste_id,mois' },
    );
  if (error) throw new Error(error.message);
}

// ----- Ventes -----------------------------------------------------------------

// ----- Détail d'une case de la grille ----------------------------------------
// « Encaissements : 98 000 € en octobre » ne dit pas de quoi ce total est fait.
// Le détail l'explique, et c'est LUI qui fait foi : le total de la case est
// recalculé à chaque modification, pour que les deux ne puissent pas diverger.

export interface TresoDetail {
  id: string;
  posteId: string;
  mois: number;
  libelle: string;
  montant: number;
  ordre: number;
}

/** Toutes les lignes de détail d'une année, rangées par poste et par mois. */
export async function listTresoDetails(annee: number): Promise<TresoDetail[]> {
  const postes = await listTresoPostes(annee);
  if (postes.length === 0) return [];
  const { data, error } = await supabase
    .from('treso_details')
    .select('id,poste_id,mois,libelle,montant,ordre')
    .eq('organization_id', ORG_ID)
    .in(
      'poste_id',
      postes.map((p) => p.id),
    )
    .order('mois')
    .order('ordre')
    .overrideTypes<
      {
        id: string;
        poste_id: string;
        mois: number;
        libelle: string;
        montant: number | string;
        ordre: number;
      }[],
      { merge: false }
    >();
  if (error) throw new Error(error.message);
  return data.map((d) => ({
    id: d.id,
    posteId: d.poste_id,
    mois: d.mois,
    libelle: d.libelle,
    montant: Number(d.montant),
    ordre: d.ordre,
  }));
}

/**
 * Recale le total de la case sur la somme de son détail.
 *
 * Appelé après chaque ajout, modification ou suppression : le total affiché
 * dans la grille ne peut pas contredire le détail qui le compose.
 */
async function recalculerCase(posteId: string, mois: number): Promise<number> {
  const { data, error } = await supabase
    .from('treso_details')
    .select('montant')
    .eq('organization_id', ORG_ID)
    .eq('poste_id', posteId)
    .eq('mois', mois)
    .overrideTypes<{ montant: number | string }[], { merge: false }>();
  if (error) throw new Error(error.message);
  const total = data.reduce((t, d) => t + Number(d.montant), 0);
  await upsertTresoMontant(posteId, mois, total);
  return total;
}

export async function createTresoDetail(
  posteId: string,
  mois: number,
  libelle: string,
  montant: number,
  ordre: number,
): Promise<void> {
  const { error } = await supabase
    .from('treso_details')
    .insert({ organization_id: ORG_ID, poste_id: posteId, mois, libelle, montant, ordre });
  if (error) throw new Error(error.message);
  await recalculerCase(posteId, mois);
}

export async function updateTresoDetail(
  id: string,
  posteId: string,
  mois: number,
  champs: { libelle?: string; montant?: number },
): Promise<void> {
  const { error } = await supabase.from('treso_details').update(champs).eq('id', id);
  if (error) throw new Error(error.message);
  await recalculerCase(posteId, mois);
}

export async function deleteTresoDetail(id: string, posteId: string, mois: number): Promise<void> {
  const { error } = await supabase.from('treso_details').delete().eq('id', id);
  if (error) throw new Error(error.message);
  await recalculerCase(posteId, mois);
}

export interface VenteExterne {
  id: string;
  numero: string;
  type: 'facture' | 'avoir';
  date_emission: string;
  base_55: string;
  base_10: string;
  base_20: string;
  base_exo: string;
  source: string;
}

export async function listVentesExternes(annee: number): Promise<VenteExterne[]> {
  const { data, error } = await supabase
    .from('ventes_externes')
    .select('id,numero,type,date_emission,base_55,base_10,base_20,base_exo,source')
    .gte('date_emission', `${String(annee)}-01-01`)
    .lte('date_emission', `${String(annee)}-12-31`)
    .is('deleted_at', null)
    .order('date_emission')
    .overrideTypes<VenteExterne[], { merge: false }>();
  if (error) throw new Error(error.message);
  return data;
}

export async function createVenteExterne(v: {
  numero: string;
  type: 'facture' | 'avoir';
  date_emission: string;
}): Promise<void> {
  const { error } = await supabase
    .from('ventes_externes')
    .insert({ organization_id: ORG_ID, source: 'manuel', ...v });
  if (error) throw new Error(error.message);
}

export async function updateVenteExterne(
  id: string,
  patch: Partial<Pick<VenteExterne, 'numero' | 'type' | 'date_emission'>> & {
    base_55?: number;
    base_10?: number;
    base_20?: number;
    base_exo?: number;
  },
): Promise<void> {
  const { error } = await supabase.from('ventes_externes').update(patch).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteVenteExterne(id: string): Promise<void> {
  const { error } = await supabase.from('ventes_externes').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export interface FactureCrmVente {
  numero: string | null;
  kind: string;
  statut: string;
  date_emission: string | null;
  montant_ht: string;
  montant_tva: string;
  montant_ttc: string;
}

export async function listFacturesCrmVentes(annee: number): Promise<FactureCrmVente[]> {
  const { data, error } = await supabase
    .from('factures')
    .select('numero,kind,statut,date_emission,montant_ht,montant_tva,montant_ttc')
    .neq('statut', 'brouillon')
    .gte('date_emission', `${String(annee)}-01-01`)
    .lte('date_emission', `${String(annee)}-12-31`)
    .overrideTypes<FactureCrmVente[], { merge: false }>();
  if (error) throw new Error(error.message);
  return data;
}

// ----- Analyse de fichiers (import initial) -----------------------------------

// « 5 685,00 € » -> 5685 ; cellule vide -> null. Tolère les espaces insécables
// et les symboles € mal encodés (export Windows).

export interface LignePrevisionnel {
  categorie: string;
  poste: string;
  valeurs: (number | null)[]; // 12 mois
}

// Analyse la grille (feuille Excel ou CSV « ; ») : postes ligne par ligne, les
// sous-totaux et totaux sont IGNORÉS (le CRM les recalcule toujours juste).
export function analyserGrillePrevisionnel(rows: unknown[][]): LignePrevisionnel[] {
  const bornes: { motif: RegExp; fin: string }[] = [
    { motif: /^SOUS\s*TOTAL\s*PERSONNEL/i, fin: 'personnel' },
    { motif: /^SOUS\s*TOTAL\s*PUB/i, fin: 'pub' },
    { motif: /^SOUS\s*TOTAL\s*VEHICULES/i, fin: 'vehicules' },
    { motif: /^SOUS\s*TOTAL\s*AUTRES/i, fin: 'autres' },
  ];
  const lignes: LignePrevisionnel[] = [];
  let categorie = 'personnel';
  let enTeteVu = false;

  for (const row of rows) {
    const label = cellTexte(row[0]).trim();
    if (!enTeteVu) {
      if (/^POSTE$/i.test(label)) enTeteVu = true;
      continue;
    }
    if (label === '') continue;

    const borne = bornes.find((b) => b.motif.test(label));
    if (borne) {
      // Fin de la catégorie courante -> on passe à la suivante.
      const ordre = ['personnel', 'pub', 'vehicules', 'autres'];
      const suivante = ordre[ordre.indexOf(borne.fin) + 1];
      categorie = suivante ?? 'exceptionnel';
      continue;
    }
    if (/^SOUS\s*TOTAL\s*FRAIS\s*FIXES/i.test(label)) {
      categorie = 'exceptionnel';
      continue;
    }
    if (/^SOUS\s*TOTAL/i.test(label) || /^TOTAL/i.test(label)) continue;
    if (/^FRAIS\s*EXCEPTIONNELS/i.test(label)) {
      categorie = 'fournisseurs';
      lignes.push({
        categorie: 'exceptionnel',
        poste: 'FRAIS EXCEPTIONNELS',
        valeurs: extraire(row),
      });
      continue;
    }
    if (/^ACOMPTES/i.test(label)) {
      lignes.push({ categorie: 'encaissements', poste: label, valeurs: extraire(row) });
      continue;
    }
    if (/^SOLDE/i.test(label)) {
      lignes.push({ categorie: 'solde', poste: label, valeurs: extraire(row) });
      continue;
    }
    if (/^TVA$/i.test(label) || /^Export\s/i.test(label)) continue;
    lignes.push({ categorie, poste: label, valeurs: extraire(row) });
  }
  return lignes;

  function extraire(row: unknown[]): (number | null)[] {
    return Array.from({ length: 12 }, (_, i) => parseMontantCellule(row[i + 1]));
  }
}

export async function importerPrevisionnel(
  annee: number,
  lignes: LignePrevisionnel[],
): Promise<number> {
  let ordre = 0;
  for (const l of lignes) {
    ordre += 10;
    const { data, error } = await supabase
      .from('treso_postes')
      .upsert(
        { organization_id: ORG_ID, annee, categorie: l.categorie, poste: l.poste, ordre },
        { onConflict: 'organization_id,annee,poste' },
      )
      .select('id')
      .overrideTypes<{ id: string }[], { merge: false }>();
    if (error) throw new Error(`Poste « ${l.poste} » : ${error.message}`);
    const posteId = data[0]?.id;
    if (!posteId) throw new Error(`Poste « ${l.poste} » : identifiant introuvable après import`);
    const montants = l.valeurs
      .map((montant, i) => ({ montant, mois: i + 1 }))
      .filter((m): m is { montant: number; mois: number } => m.montant !== null)
      .map((m) => ({
        organization_id: ORG_ID,
        poste_id: posteId,
        mois: m.mois,
        montant: m.montant,
      }));
    if (montants.length > 0) {
      const { error: e2 } = await supabase
        .from('treso_montants')
        .upsert(montants, { onConflict: 'poste_id,mois' });
      if (e2) throw new Error(`Montants « ${l.poste} » : ${e2.message}`);
    }
  }
  return lignes.length;
}

export async function importerVentes(lignes: LigneVenteImport[]): Promise<number> {
  if (lignes.length === 0) return 0;
  const { error } = await supabase.from('ventes_externes').upsert(
    lignes.map((l) => ({ organization_id: ORG_ID, ...l })),
    { onConflict: 'organization_id,numero' },
  );
  if (error) throw new Error(error.message);
  return lignes.length;
}

// ----- Lecture des fichiers (CSV « ; » ou Excel via SheetJS chargé à la volée) -

export async function lireFichierGrille(file: File): Promise<unknown[][]> {
  if (/\.csv$/i.test(file.name)) {
    const texte = await file.text();
    return texte.split(/\r?\n/).map((l) => l.split(';'));
  }
  const [feuilles] = await lireFeuillesExcel(file, 1);
  return feuilles ?? [];
}

export async function lireFeuillesExcel(file: File, max?: number): Promise<unknown[][][]> {
  const XLSX = await import('xlsx');
  // Volontairement SANS `cellDates` : les dates restent des nombres Excel, que
  // versDateIso convertit en UTC. Les objets Date construits par la
  // bibliothèque dépendent du fuseau du navigateur et décalaient les dates
  // d'un jour à Paris.
  const wb = XLSX.read(await file.arrayBuffer(), {});
  const noms = max ? wb.SheetNames.slice(0, max) : wb.SheetNames;
  return noms
    .map((nom) => wb.Sheets[nom])
    .filter((f): f is NonNullable<typeof f> => Boolean(f))
    .map((feuille) => XLSX.utils.sheet_to_json<unknown[]>(feuille, { header: 1, defval: null }));
}

/** D'où viennent les mouvements : tableur tenu à la main, ou vrai relevé de banque. */
export interface AnalyseReleveFichier extends AnalyseMouvements {
  provenance: 'tableur' | 'ofx' | 'csv' | 'classeur';
  /** Solde annoncé par la banque : sert à vérifier, jamais à écrire. */
  soldeFinalBanque: number | null;
  /** Compte importé, et comptes du même fichier laissés de côté. */
  compte: string | null;
  autresComptes: string[];
  /** Période couverte, quand le relevé l'annonce : borne le rapprochement. */
  periode: { debut: string; fin: string } | null;
  /** Lignes écartées faute de date ou de montant (totaux, pieds de page…). */
  ignorees: number;
}

/** Le relevé porte-t-il bien sur l'année affichée ? */
function memeAnnee(debutPeriode: string | null, annee: number): boolean {
  return debutPeriode?.startsWith(String(annee)) ?? false;
}

/** Met une analyse de relevé bancaire à la forme commune attendue par l'import. */
function depuisReleve(r: AnalyseReleve, annee: number): AnalyseReleveFichier {
  return {
    mouvements: r.mouvements,
    // Le solde de départ n'est repris que si le relevé part du 1er janvier de
    // l'année affichée : sur une période partielle, ou sur une autre année, ce
    // chiffre serait faux. L'import ne l'écrit de toute façon qu'à vide.
    soldeInitial: memeAnnee(r.debutPeriode, annee) ? r.soldeInitialDeduit : null,
    datesCorrigees: 0,
    sansDate: 0,
    provenance: r.format,
    soldeFinalBanque: r.soldeFinalBanque,
    compte: r.compte,
    autresComptes: r.autresComptes,
    periode: r.debutPeriode && r.finPeriode ? { debut: r.debutPeriode, fin: r.finPeriode } : null,
    ignorees: r.ignorees,
  };
}

/**
 * Analyse un fichier de relevé, quelle que soit sa nature.
 *
 * Un seul bouton dans l'écran : l'utilisateur n'a pas à savoir s'il dépose le
 * tableur de suivi tenu à la main ou le relevé téléchargé depuis sa banque.
 *
 * Le tri se fait sur le CONTENU, pas sur l'extension : les deux arrivent en
 * .xlsx et n'ont rien à voir dans leur disposition. On cherche donc d'abord un
 * relevé de banque, et on se rabat sur le tableur de suivi.
 */
export async function analyserFichierReleve(
  file: File,
  annee: number,
): Promise<AnalyseReleveFichier> {
  if (/\.(xlsx|xls)$/i.test(file.name)) {
    const feuilles = await lireFeuillesExcel(file);
    const banque = analyserClasseurBancaire(feuilles);
    if (banque) return depuisReleve(banque, annee);

    const base = analyserFeuillesMouvements(feuilles);
    return {
      ...base,
      provenance: 'tableur',
      soldeFinalBanque: null,
      compte: null,
      autresComptes: [],
      periode: null,
      ignorees: 0,
    };
  }

  return depuisReleve(
    analyserReleveBancaire(decoderTexteBancaire(await file.arrayBuffer())),
    annee,
  );
}

// ----- Exports Excel (téléchargés par le navigateur) --------------------------

type Aoa = (string | number | null)[][];

function sommeValeurs(vals: (number | null)[]): number {
  return vals.reduce<number>((s, x) => s + (x ?? 0), 0);
}

function valeursPoste(p: TresoPoste): (number | null)[] {
  const v: (number | null)[] = Array.from({ length: 12 }, () => null);
  for (const m of p.treso_montants) v[m.mois - 1] = Number(m.montant);
  return v;
}

const MOIS_PLEINS = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];

/** Déclenche le téléchargement d'un CSV lisible dans Excel (UTF-8 avec repère, point-virgule). */
function telechargerCsv(nom: string, lignes: Aoa): void {
  const blob = new Blob([BOM_UTF8 + csvDepuisLignes(lignes)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nom;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Exporte la grille prévisionnelle en CSV (sous-totaux et total recalculés). */
export function exporterGrilleCsv(annee: number, postes: TresoPoste[]): void {
  const lignes: Aoa = [['Poste', ...MOIS_PLEINS, 'Annuel']];
  const totalFrais: number[] = Array.from({ length: 12 }, () => 0);
  for (const cat of CATEGORIES) {
    const liste = postes.filter((p) => p.categorie === cat.id);
    if (liste.length === 0) continue;
    if (cat.type === 'charge') lignes.push([cat.label.toUpperCase()]);
    const st: number[] = Array.from({ length: 12 }, () => 0);
    for (const p of liste) {
      const vals = valeursPoste(p);
      vals.forEach((v, i) => {
        st[i] = (st[i] ?? 0) + (v ?? 0);
      });
      lignes.push([p.poste, ...vals, sommeValeurs(vals)]);
    }
    if (cat.type === 'charge') {
      lignes.push([`SOUS-TOTAL ${cat.label.toUpperCase()}`, ...st, sommeValeurs(st)]);
      st.forEach((v, i) => {
        totalFrais[i] = (totalFrais[i] ?? 0) + v;
      });
    }
  }
  lignes.push(['TOTAL DES FRAIS', ...totalFrais, sommeValeurs(totalFrais)]);
  telechargerCsv(`previsionnel-${String(annee)}.csv`, lignes);
}

export interface SyntheseMois {
  nb: number;
  ht: number;
  tva: number;
  ttc: number;
}

/**
 * Exporte les ventes en CSV : deux fichiers, la synthèse mensuelle et le
 * détail de toutes les pièces (une ligne par facture ou avoir, avec le mois,
 * les bases par taux de TVA, le HT, la TVA et le TTC).
 */
export function exporterVentesCsv(
  annee: number,
  synthese: SyntheseMois[],
  total: SyntheseMois,
  externes: VenteExterne[],
  crm: FactureCrmVente[],
): void {
  const arrondi = (n: number) => Math.round(n * 100) / 100;
  const feuilleSynthese: Aoa = [
    ['Mois', 'Pièces', 'Total HT', 'TVA', 'Total TTC'],
    ...synthese.map((m, i) => [
      MOIS_PLEINS[i] ?? '',
      m.nb,
      arrondi(m.ht),
      arrondi(m.tva),
      arrondi(m.ttc),
    ]),
    ['TOTAL', total.nb, arrondi(total.ht), arrondi(total.tva), arrondi(total.ttc)],
  ];

  interface Piece {
    mois: number;
    type: string;
    numero: string;
    date: string;
    b55: number | null;
    b10: number | null;
    b20: number | null;
    bexo: number | null;
    totalHt: number;
    tva: number;
    ttc: number;
  }
  const pieces: Piece[] = [];
  const moisDe = (date: string): number => Number(date.slice(5, 7)) - 1;
  for (const v of externes) {
    const b55 = Number(v.base_55);
    const b10 = Number(v.base_10);
    const b20 = Number(v.base_20);
    const bexo = Number(v.base_exo);
    pieces.push({
      mois: moisDe(v.date_emission),
      type: v.type === 'avoir' ? 'Avoir' : 'Facture',
      numero: v.numero,
      date: v.date_emission,
      b55: arrondi(b55),
      b10: arrondi(b10),
      b20: arrondi(b20),
      bexo: arrondi(bexo),
      totalHt: arrondi(b55 + b10 + b20 + bexo),
      tva: arrondi(b55 * 0.055 + b10 * 0.1 + b20 * 0.2),
      ttc: arrondi(b55 * 1.055 + b10 * 1.1 + b20 * 1.2 + bexo),
    });
  }
  for (const f of crm) {
    if (!f.date_emission) continue;
    pieces.push({
      mois: moisDe(f.date_emission),
      type: f.kind === 'avoir' ? 'Avoir (CRM)' : 'Facture (CRM)',
      numero: f.numero ?? '(brouillon)',
      date: f.date_emission,
      b55: null,
      b10: null,
      b20: null,
      bexo: null,
      totalHt: arrondi(Number(f.montant_ht)),
      tva: arrondi(Number(f.montant_tva)),
      ttc: arrondi(Number(f.montant_ttc)),
    });
  }
  pieces.sort((a, b) => a.date.localeCompare(b.date) || a.numero.localeCompare(b.numero));
  const signe = (p: Piece): number => (p.type.startsWith('Avoir') ? -1 : 1);
  const somme = (f: (p: Piece) => number | null): number =>
    arrondi(pieces.reduce((s, p) => s + signe(p) * (f(p) ?? 0), 0));
  const detail: Aoa = [
    [
      'Mois',
      'Type',
      'N° pièce',
      'Date',
      'HT 5,5 %',
      'HT 10 %',
      'HT 20 %',
      'HT exo',
      'Total HT',
      'TVA',
      'Total TTC',
    ],
    ...pieces.map((p) => [
      MOIS_PLEINS[p.mois] ?? '',
      p.type,
      p.numero,
      p.date,
      p.b55,
      p.b10,
      p.b20,
      p.bexo,
      p.totalHt,
      p.tva,
      p.ttc,
    ]),
    [
      'TOTAL (avoirs déduits)',
      '',
      '',
      '',
      somme((p) => p.b55),
      somme((p) => p.b10),
      somme((p) => p.b20),
      somme((p) => p.bexo),
      somme((p) => p.totalHt),
      somme((p) => p.tva),
      somme((p) => p.ttc),
    ],
  ];
  telechargerCsv(`ventes-${String(annee)}-synthese.csv`, feuilleSynthese);
  telechargerCsv(`ventes-${String(annee)}-pieces.csv`, detail);
}

// ----- Suivi bancaire (relevé de compte) --------------------------------------
// Le solde n'est jamais stocké : il se recalcule à partir du solde initial de
// l'année et du cumul des mouvements, donc il reste juste après toute
// correction — contrairement à une colonne figée dans un tableur.

export interface Mouvement {
  id: string;
  date_mouvement: string;
  libelle: string;
  debit: string;
  credit: string;
  pointe: boolean;
  ordre: number;
  /**
   * Poste imposé à la main, par son nom. `null` = classement automatique.
   *
   * Certains mouvements ne nomment personne : un chèque émis, un effet domicilié
   * ne portent qu'un numéro, et aucune règle ne les reconnaîtra jamais. C'est
   * le seul moyen de les ranger, et il l'emporte sur toutes les règles.
   */
  poste_manuel: string | null;
}

const COLONNES_MOUVEMENT = 'id,date_mouvement,libelle,debit,credit,pointe,ordre,poste_manuel';
/** Avant la migration 28, `poste_manuel` n'existe pas. */
const COLONNES_MOUVEMENT_AVANT_28 = 'id,date_mouvement,libelle,debit,credit,pointe,ordre';

export async function listMouvements(annee: number): Promise<Mouvement[]> {
  // Lu par tranches : une année dépasse le millier de mouvements (1 080 en
  // 2026), et une lecture tronquée fausserait tous les soldes en silence.
  const lire = (colonnes: string): Promise<Mouvement[]> =>
    lireTout<Mouvement>((de, a) =>
      supabase
        .from('treso_mouvements')
        .select(colonnes)
        .gte('date_mouvement', `${String(annee)}-01-01`)
        .lte('date_mouvement', `${String(annee)}-12-31`)
        .is('deleted_at', null)
        .order('date_mouvement')
        .order('ordre')
        .range(de, a)
        .overrideTypes<Mouvement[], { merge: false }>(),
    );
  try {
    return await lire(COLONNES_MOUVEMENT);
  } catch (e) {
    if (e instanceof Error && colonneAbsente(e.message)) {
      const avant = await lire(COLONNES_MOUVEMENT_AVANT_28);
      return avant.map((m) => ({ ...m, poste_manuel: null }));
    }
    throw e;
  }
}

export async function getSoldeInitial(annee: number): Promise<number> {
  const { data, error } = await supabase
    .from('treso_soldes')
    .select('solde_initial')
    .eq('annee', annee)
    .overrideTypes<{ solde_initial: string }[], { merge: false }>();
  if (error) throw new Error(error.message);
  return Number(data[0]?.solde_initial ?? 0);
}

export async function setSoldeInitial(annee: number, montant: number): Promise<void> {
  const { error } = await supabase
    .from('treso_soldes')
    .upsert(
      { organization_id: ORG_ID, annee, solde_initial: montant },
      { onConflict: 'organization_id,annee' },
    );
  if (error) throw new Error(error.message);
}

export async function createMouvement(m: {
  date_mouvement: string;
  libelle: string;
  debit: number;
  credit: number;
}): Promise<void> {
  const { error } = await supabase
    .from('treso_mouvements')
    .insert({ organization_id: ORG_ID, ordre: 9999, ...m });
  if (error) throw new Error(error.message);
}

export async function updateMouvement(
  id: string,
  patch: {
    date_mouvement?: string;
    libelle?: string;
    debit?: number;
    credit?: number;
    pointe?: boolean;
  },
): Promise<void> {
  const { error } = await supabase.from('treso_mouvements').update(patch).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteMouvement(id: string): Promise<void> {
  const { error } = await supabase.from('treso_mouvements').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * Range un mouvement à la main dans un poste, ou libère le classement (`null`).
 *
 * Le poste est désigné par son NOM, comme dans les règles, et pour la même
 * raison : les postes sont recréés chaque année, un identifiant mourrait au
 * 1er janvier.
 */
export async function setPosteManuel(id: string, poste: string | null): Promise<void> {
  const { error } = await supabase
    .from('treso_mouvements')
    .update({ poste_manuel: poste === null || poste.trim() === '' ? null : poste.trim() })
    .eq('id', id);
  if (error) {
    if (colonneAbsente(error.message))
      throw new Error(
        'Mise à jour de la base en attente : ranger un mouvement à la main ne sera ' +
          'possible qu\'après la migration 28.',
      );
    throw new Error(error.message);
  }
}

export interface MouvementImport {
  date_mouvement: string;
  libelle: string;
  debit: number;
  credit: number;
  pointe: boolean;
  ordre: number;
}

export interface AnalyseMouvements {
  mouvements: MouvementImport[];
  soldeInitial: number | null;
  /** Dates hors du mois de la feuille, recalées sur ce mois (fautes de frappe). */
  datesCorrigees: number;
  /** Lignes sans date, rattachées au 1er du mois de la feuille (prévisions). */
  sansDate: number;
}

const MOIS_NOMS = [
  'JANVIER',
  'FEVRIER',
  'MARS',
  'AVRIL',
  'MAI',
  'JUIN',
  'JUILLET',
  'AOUT',
  'SEPTEMBRE',
  'OCTOBRE',
  'NOVEMBRE',
  'DECEMBRE',
];

function sansAccents(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();
}

/**
 * Ramène un jour dans les bornes réelles du mois visé : un 31 recalé sur un
 * mois de 30 jours (ou sur février) donnerait sinon une date inexistante.
 */
function jourDansLeMois(annee: number, mois0: number, jour: number): number {
  const dernierJour = new Date(annee, mois0 + 1, 0).getDate();
  return Math.min(Math.max(Number.isFinite(jour) ? jour : 1, 1), dernierJour);
}

/** Mois (0-11) et année portés par le titre « SUIVI TRESORERIE AOÛT 2026 ». */
function moisDeLaFeuille(rows: unknown[][]): { mois: number; annee: number } | null {
  for (const row of rows.slice(0, 6)) {
    for (const cell of row.slice(0, 3)) {
      const t = sansAccents(cellTexte(cell));
      if (!t.includes('TRESORERIE')) continue;
      const mois = MOIS_NOMS.findIndex((m) => t.includes(m));
      const an = /(\d{4})/.exec(t);
      if (mois >= 0) return { mois, annee: an ? Number(an[1]) : new Date().getFullYear() };
    }
  }
  return null;
}

/**
 * Analyse les feuilles mensuelles du classeur « SUIVI TRESORERIE ».
 * Chaque feuille : une ligne d'en-tête DATE | LIBELLÉ | DÉBIT | CRÉDIT | SOLDE,
 * puis le solde de départ, puis les mouvements. La date n'est portée que par la
 * première ligne d'une journée : elle est reportée sur les suivantes.
 * La feuille « PRÉVISIONNEL » (grille de charges) est ignorée.
 */
export function analyserFeuillesMouvements(feuilles: unknown[][][]): AnalyseMouvements {
  const mouvements: MouvementImport[] = [];
  // Solde d'ouverture de chaque feuille, retenu avec le mois de la feuille :
  // celui du mois le plus ancien devient le solde initial de l'année.
  const ouvertures: { cle: string; solde: number }[] = [];
  let datesCorrigees = 0;
  let sansDate = 0;

  for (const rows of feuilles) {
    const iEntete = rows.findIndex((r) => cellTexte(r[0]).trim().toUpperCase() === 'DATE');
    if (iEntete === -1) continue; // pas une feuille de relevé (ex. PRÉVISIONNEL)
    const periode = moisDeLaFeuille(rows);
    if (!periode) continue;

    const prefixe = `${String(periode.annee)}-${String(periode.mois + 1).padStart(2, '0')}`;
    const premierDuMois = `${prefixe}-01`;
    let dateCourante: string | null = null;
    let soldeOuverture: number | null = null;
    let ordre = 0;

    for (const row of rows.slice(iEntete + 1)) {
      const brut = versDateIso(row[0]);
      if (brut) {
        // Une date hors du mois de la feuille est une faute de frappe : on la
        // recale sur le bon mois en gardant le jour (Excel l'acceptait en
        // silence, et elle contaminait toutes les lignes suivantes).
        if (brut.startsWith(prefixe)) dateCourante = brut;
        else {
          const jour = jourDansLeMois(periode.annee, periode.mois, Number(brut.slice(8, 10)));
          dateCourante = `${prefixe}-${String(jour).padStart(2, '0')}`;
          datesCorrigees += 1;
        }
      }
      const libelle = cellTexte(row[1]).trim();
      const debit = parseMontantCellule(row[2]) ?? 0;
      const credit = parseMontantCellule(row[3]) ?? 0;
      const solde = parseMontantCellule(row[4]);

      // La première ligne chiffrée sans libellé porte le solde de départ du mois.
      if (libelle === '' && soldeOuverture === null && solde !== null) {
        soldeOuverture = solde;
        continue;
      }
      if (libelle === '' || (debit === 0 && credit === 0)) continue;

      // Les mois à venir sont saisis sans date (prévisions) : on les rattache
      // au 1er du mois de la feuille pour conserver la projection du solde.
      if (dateCourante === null) sansDate += 1;

      ordre += 1;
      // Dernier filet : quoi qu'il arrive, on n'envoie jamais une date
      // impossible à la base (un 31 sur un mois de 30 jours, par exemple).
      const dateRetenue = dateCourante ?? premierDuMois;
      mouvements.push({
        date_mouvement: dateExiste(dateRetenue) ? dateRetenue : premierDuMois,
        libelle,
        debit,
        credit,
        pointe: sansAccents(cellTexte(row[6]).trim()) === 'OK',
        ordre,
      });
    }

    if (soldeOuverture !== null) ouvertures.push({ cle: prefixe, solde: soldeOuverture });
  }

  mouvements.sort((a, b) =>
    a.date_mouvement === b.date_mouvement
      ? a.ordre - b.ordre
      : a.date_mouvement.localeCompare(b.date_mouvement),
  );
  ouvertures.sort((a, b) => a.cle.localeCompare(b.cle));
  return { mouvements, soldeInitial: ouvertures[0]?.solde ?? null, datesCorrigees, sansDate };
}

export interface ResultatImportMouvements {
  ajoutes: number;
  /** Déjà présents au mot près. */
  dejaPresents: number;
  /**
   * Même date et même montant, libellé différent : la même opération, écrite
   * autrement de part et d'autre. Reconnue, donc jamais dupliquée.
   */
  rapproches: { date: string; libelleCrm: string; libelleBanque: string }[];
  /**
   * Inscrits dans le CRM sur la période du relevé, mais absents de celui-ci.
   * Ce sont eux qui méritent un coup d'œil.
   */
  absents: { date: string; libelle: string; debit: number; credit: number }[];
  supprimes: number;
}

/**
 * Importe un relevé, en le CROISANT avec le tableau du compte bancaire.
 *
 * Le rapprochement se fait sur la date et le montant, jamais sur le libellé :
 * la même opération est décrite autrement selon qu'elle a été saisie à la main
 * (« Facture EDF ») ou reprise de la banque (« Prélèvement - EDF SA - REF… »).
 * Comparer les mots créerait un doublon à chaque import.
 *
 * Par défaut le mode est INCRÉMENTAL : seules les lignes vraiment absentes sont
 * ajoutées, et tout ce que l'utilisateur a fait (pointages, corrections,
 * saisies manuelles) est conservé.
 *
 * `remplacerTout` n'est destiné qu'au premier chargement.
 */
export async function importerMouvements(
  annee: number,
  mouvements: MouvementImport[],
  soldeInitial: number | null,
  remplacerTout = false,
  periode?: { debut: string; fin: string } | null,
): Promise<ResultatImportMouvements> {
  let supprimes = 0;
  let aInserer = mouvements;
  let dejaPresents = 0;
  let rapproches: ResultatImportMouvements['rapproches'] = [];
  let absents: ResultatImportMouvements['absents'] = [];

  if (remplacerTout) {
    const existants = await listMouvements(annee);
    supprimes = existants.length;
    const { error: eDel } = await supabase
      .from('treso_mouvements')
      .delete()
      .gte('date_mouvement', `${String(annee)}-01-01`)
      .lte('date_mouvement', `${String(annee)}-12-31`);
    if (eDel) throw new Error(eDel.message);
  } else {
    const existants = await listMouvements(annee);
    const bilan = rapprocherMouvements(existants, mouvements, periode);
    aInserer = bilan.nouveaux;
    dejaPresents = bilan.identiques.length;
    rapproches = bilan.rapproches.map((p) => ({
      date: p.crm.date_mouvement,
      libelleCrm: p.crm.libelle,
      libelleBanque: p.releve.libelle,
    }));
    absents = bilan.absents.map((c) => ({
      date: c.date_mouvement,
      libelle: c.libelle,
      debit: Number(c.debit),
      credit: Number(c.credit),
    }));
  }

  for (let i = 0; i < aInserer.length; i += 500) {
    const lot = aInserer.slice(i, i + 500).map((m) => ({ organization_id: ORG_ID, ...m }));
    const { error } = await supabase.from('treso_mouvements').insert(lot);
    if (error) throw new Error(error.message);
  }
  // Le solde de départ n'est posé qu'au premier chargement : une reprise
  // quotidienne ne doit pas écraser une valeur ajustée à la main.
  if (soldeInitial !== null && (remplacerTout || (await getSoldeInitial(annee)) === 0)) {
    await setSoldeInitial(annee, soldeInitial);
  }
  return { ajoutes: aInserer.length, dejaPresents, rapproches, absents, supprimes };
}

// ----- Règles de rapprochement banque / grille ------------------------------
//
// Elles disent quel libellé de relevé alimente quel poste. Stockées par NOM de
// poste et non par identifiant : les postes sont recréés chaque année, une
// règle liée à un identifiant mourrait au 1er janvier.
//
// La table peut ne pas exister encore (migration 27) : la lecture rend alors
// une liste vide plutôt que de casser l'écran.

const TABLE_REGLES = 'treso_regles_classement';

export interface RegleStockee {
  id: string;
  /** Le NOM du poste visé. */
  poste: string;
  /**
   * Un ou plusieurs fragments séparés par des virgules, tels que saisis.
   *
   * Le même fournisseur s'écrit « POINT P », « POINT-P » et « POINTP » selon
   * le jour : une seule règle doit pouvoir les couvrir, sinon il en faut trois
   * et la quatrième orthographe passe à travers.
   */
  motif: string;
  sens: SensRegle;
  ordre: number;
  /**
   * Bornes de montant, en valeur absolue, incluses. `null` = pas de borne.
   *
   * Trois prélèvements URSSAF du même mois portent le même libellé et des
   * montants différents : c'est le seul moyen de les séparer.
   */
  montant_min: number | null;
  montant_max: number | null;
}

/**
 * Les fragments d'une règle, tels que le moteur les attend.
 *
 * Une virgule sépare deux orthographes du même tiers. Un fragment vide
 * reconnaîtrait tout : il est écarté ici plutôt que de ramasser le relevé
 * entier.
 */
export function motifsDe(motif: string): string[] {
  return motif
    .split(',')
    .map((x) => x.trim())
    .filter((x) => x !== '');
}

interface RegleBrute {
  id: string;
  poste: string;
  motif: string;
  sens: string;
  ordre: number;
  montant_min?: string | number | null;
  montant_max?: string | number | null;
}

/** `numeric` revient en chaîne de PostgREST ; une borne absente reste `null`. */
function borne(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export async function listReglesClassement(): Promise<RegleStockee[]> {
  const lire = async (colonnes: string): Promise<RegleBrute[] | 'table-absente'> => {
    const { data, error } = await supabase
      .from('treso_regles_classement')
      .select(colonnes)
      .eq('organization_id', ORG_ID)
      .is('deleted_at', null)
      .order('ordre')
      .overrideTypes<RegleBrute[], { merge: false }>();
    if (error) {
      if (tableAbsente(error.message, TABLE_REGLES)) return 'table-absente';
      throw new Error(error.message);
    }
    return data;
  };

  // Les bornes arrivent avec la migration 28 : sans elles la règle ne regarde
  // que le libellé, ce qui est exactement son comportement d'avant.
  let brutes: RegleBrute[] | 'table-absente';
  try {
    brutes = await lire('id,poste,motif,sens,ordre,montant_min,montant_max');
  } catch (e) {
    if (!(e instanceof Error) || !colonneAbsente(e.message)) throw e;
    brutes = await lire('id,poste,motif,sens,ordre');
  }
  if (brutes === 'table-absente') return [];

  return brutes.map((r) => ({
    id: r.id,
    poste: r.poste,
    motif: r.motif,
    sens: r.sens === 'credit' ? 'credit' : 'debit',
    ordre: r.ordre,
    montant_min: borne(r.montant_min),
    montant_max: borne(r.montant_max),
  }));
}

/**
 * Les règles sous la forme attendue par le moteur de classement.
 *
 * Une règle dont le poste n'existe pas dans l'année affichée est ÉCARTÉE : elle
 * viserait un poste absent de la grille, et son montant n'aurait nulle part où
 * s'afficher. L'écran la signale à part plutôt que de la faire disparaître.
 */
export function reglesPourAnnee(
  regles: readonly RegleStockee[],
  postes: readonly TresoPoste[],
): { utilisables: RegleClassement[]; orphelines: RegleStockee[] } {
  const parNom = new Map(postes.map((p) => [p.poste, p.id]));
  const utilisables: RegleClassement[] = [];
  const orphelines: RegleStockee[] = [];
  for (const r of regles) {
    const posteId = parNom.get(r.poste);
    if (posteId === undefined) {
      orphelines.push(r);
      continue;
    }
    const motifs = motifsDe(r.motif);
    // Une règle sans aucun fragment reconnaîtrait tout le relevé : on la traite
    // comme inapplicable plutôt que de lui laisser ramasser l'année entière.
    if (motifs.length === 0) {
      orphelines.push(r);
      continue;
    }
    utilisables.push({
      id: r.id,
      posteId,
      motifs,
      sens: r.sens,
      ordre: r.ordre,
      montantMin: r.montant_min,
      montantMax: r.montant_max,
    });
  }
  return { utilisables, orphelines };
}

/** Traduit en français ce que la base refuse, plutôt qu'un message technique. */
function erreurRegle(message: string, motif: string): Error {
  // Deux index selon l'état de la base : avant la migration 28 l'unicité porte
  // sur le seul libellé, après elle porte aussi sur les bornes de montant.
  if (message.includes('treso_regles_motif_bornes_unique'))
    return new Error(
      `Une règle existe déjà pour « ${motif} » avec les mêmes montants. ` +
        'Changez la borne basse ou haute pour la distinguer.',
    );
  if (message.includes('treso_regles_motif_unique'))
    return new Error(`Une règle existe déjà pour « ${motif} ».`);
  if (message.includes('treso_regles_motif_utile'))
    return new Error('Un motif doit faire au moins deux caractères.');
  if (message.includes('treso_regles_bornes'))
    return new Error('Le montant minimum doit être positif et ne pas dépasser le maximum.');
  if (colonneAbsente(message)) return new Error(BASE_EN_ATTENTE_MONTANTS);
  return new Error(message);
}

/**
 * Ce qu'on dit quand la base n'a pas encore ses colonnes.
 *
 * Le message brut de Supabase — « Could not find the 'montant_max' column of
 * 'treso_regles_classement' in the schema cache » — est arrivé tel quel sous
 * les yeux de l'utilisateur le 06/10/2026. Une erreur qu'on ne comprend pas ne
 * dit pas quoi faire, et laisse croire à une panne.
 */
const BASE_EN_ATTENTE_MONTANTS =
  'Mise à jour de la base en attente : les conditions de montant ne peuvent pas ' +
  'encore être enregistrées. Enregistrez la règle sans montant, ou demandez la ' +
  'mise à jour de la base (migration 28).';

export interface ChampsRegle {
  poste: string;
  motif: string;
  sens: SensRegle;
  ordre: number;
  montant_min: number | null;
  montant_max: number | null;
}

/**
 * Écrit une règle, et retente sans les bornes si la base ne les connaît pas.
 *
 * Une règle SANS condition de montant n'a aucune raison d'être refusée parce
 * que la migration 28 n'est pas passée : c'est tout ce que le CRM savait faire
 * jusqu'ici. Envoyer `montant_min: null` suffisait pourtant à la faire refuser,
 * et plus aucune règle n'était créable. On retente donc sans ces deux champs.
 *
 * Mais seulement quand les deux bornes sont vides : une borne saisie et
 * silencieusement perdue donnerait une règle qui ramasse tout un libellé au
 * lieu d'une tranche de montants, et le total serait faux sans que rien ne le
 * signale. Dans ce cas, on refuse, et on dit pourquoi.
 */
async function ecrireRegle(
  champs: Record<string, unknown>,
  envoyer: (patch: Record<string, unknown>) => PromiseLike<{ error: { message: string } | null }>,
  motif: string,
): Promise<void> {
  const { error } = await envoyer(champs);
  if (!error) return;

  const bornesRenseignees =
    (champs.montant_min ?? null) !== null || (champs.montant_max ?? null) !== null;
  switch (suiteApresRefus(error.message, bornesRenseignees)) {
    case 'propager':
      throw erreurRegle(error.message, motif);
    case 'refuser':
      throw new Error(BASE_EN_ATTENTE_MONTANTS);
    case 'reessayer-sans': {
      const sansBornes = { ...champs };
      delete sansBornes.montant_min;
      delete sansBornes.montant_max;
      const second = await envoyer(sansBornes);
      if (second.error) throw erreurRegle(second.error.message, motif);
      return;
    }
  }
}

export async function createRegleClassement(r: ChampsRegle): Promise<void> {
  const motif = motifsDe(r.motif).join(', ');
  await ecrireRegle(
    {
      organization_id: ORG_ID,
      poste: r.poste,
      motif,
      sens: r.sens,
      ordre: r.ordre,
      montant_min: r.montant_min,
      montant_max: r.montant_max,
    },
    (patch) => supabase.from('treso_regles_classement').insert(patch),
    motif,
  );
}

export async function updateRegleClassement(
  id: string,
  champs: Partial<ChampsRegle>,
): Promise<void> {
  if (Object.keys(champs).length === 0) return;
  const motif = typeof champs.motif === 'string' ? motifsDe(champs.motif).join(', ') : '';
  const patch: Record<string, unknown> = { ...champs };
  if (typeof champs.motif === 'string') patch.motif = motif;
  await ecrireRegle(
    patch,
    (p) => supabase.from('treso_regles_classement').update(p).eq('id', id),
    motif,
  );
}

/** Suppression douce : la règle reste, pour expliquer un classement passé. */
export async function deleteRegleClassement(id: string): Promise<void> {
  const { error } = await supabase
    .from('treso_regles_classement')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(error.message);
}
